import 'src/screen/Protected/ApiKeysScreen/ApiKeysScreen.scss';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Grid, Modal, Paper, TextField, FormControl, InputLabel, Select, MenuItem, Chip, Box, Typography, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Button from 'src/component/UI/Button';
import ApiKeysList from './ApiKeysList';
import { createApiKey, clearApiKeyToken, updateApiKey } from 'src/module/apiKeys/action';
import { getApiKeyCreationLoading, getNewApiKeyToken, getNewApiKeyCert, getNewApiKeyPrivateKey } from 'src/module/apiKeys/selector';
import toast from 'src/utils/toast';
import { getPermissions } from 'src/module/permission/selector';
import { fetchPermissions } from 'src/module/permission/action';
import { rolesNameToKey } from 'src/utils/utils';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';

// Helper function to convert role names back to keys (same mapping as rolesNameToKey)
const roleNameToKey = (roleName: string): string => {
  return rolesNameToKey(roleName);
};

// Mapping from role names to translation keys
const roleNameToTranslationKey: Record<string, string> = {
  'Administrator': 'users.administrator',
  'Regular User': 'users.regularUser',
  'Maintenance User': 'users.maintenanceUser',
  'EHT Specialist': 'users.ehtSpecialist',
  'Operations': 'users.operations',
};


export default function ApiKeysScreen () {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Helper to get translated role label
  const getRoleLabel = (roleName: string): string => {
    const translationKey = roleNameToTranslationKey[roleName];
    return translationKey ? t(translationKey) : roleName;
  };

  // Helper to get translated role label from role key
  const getRoleLabelFromKey = (roleKey: string, rolesList: string[]): string => {
    const roleName = rolesList.find(role => rolesNameToKey(role) === roleKey);
    return roleName ? getRoleLabel(roleName) : roleKey;
  };
  const [isAddApiKeyModalOpen, setIsAddApiKeyModalOpen] = useState(false);
  const [isEditApiKeyModalOpen, setIsEditApiKeyModalOpen] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [apiKeyName, setApiKeyName] = useState('');
  const [keyType, setKeyType] = useState<'token' | 'x509'>('token');
  const [expiryValue, setExpiryValue] = useState<string>('');
  const [expiryUnit, setExpiryUnit] = useState<'days' | 'months' | 'years'>('months');
  const [editingApiKey, setEditingApiKey] = useState(null);
  const [editApiKeyName, setEditApiKeyName] = useState('');
  const [editSelectedRoles, setEditSelectedRoles] = useState([]);
  const permissions = useSelector(getPermissions);

  const creationLoading = useSelector(getApiKeyCreationLoading);
  const newApiKeyToken = useSelector(getNewApiKeyToken);
  const newApiKeyCert = useSelector(getNewApiKeyCert);
  const newApiKeyPrivateKey = useSelector(getNewApiKeyPrivateKey);
  const [availableRoles, setAvailableRoles] = useState([]);

  useEffect(() => {
    dispatch(fetchPermissions());
  }, []);

  useEffect(() => {
    if (permissions) {
      const allRoles = [];
      permissions.map((perm) => {
        const roles = perm.get('role');
        roles?.map(role => {
          if (!allRoles.includes(role)) {
            allRoles.push(role);
          }
        });
      });
      setAvailableRoles(allRoles);
    }
  }, [permissions]);

  const computeExpiresAt = () => {
    if (!expiryValue || isNaN(Number(expiryValue))) return null;
    const d = new Date();
    const n = Number(expiryValue);
    if (expiryUnit === 'days') d.setDate(d.getDate() + n);
    else if (expiryUnit === 'months') d.setMonth(d.getMonth() + n);
    else d.setFullYear(d.getFullYear() + n);
    return d.toISOString();
  };

  const handleCreateApiKey = () => {
    dispatch(createApiKey(selectedRoles, apiKeyName || null, keyType, computeExpiresAt()));
  };

  const handleCloseModal = () => {
    setIsAddApiKeyModalOpen(false);
    setSelectedRoles([]);
    setApiKeyName('');
    setKeyType('token');
    setExpiryValue('');
    setExpiryUnit('months');
    // Clear the token from the store when modal is closed
    if (newApiKeyToken || newApiKeyCert) {
      dispatch(clearApiKeyToken());
    }
  };

  const handleCopyToken = () => {
    if (newApiKeyToken) {
      navigator.clipboard.writeText(newApiKeyToken);
      toast.success(t('toast.apiKeyCopied'));
    }
  };

  const handleCopyCert = () => {
    if (newApiKeyCert) {
      navigator.clipboard.writeText(newApiKeyCert);
      toast.success(t('toast.apiKeyCopied'));
    }
  };

  const handleCopyPrivateKey = () => {
    if (newApiKeyPrivateKey) {
      navigator.clipboard.writeText(newApiKeyPrivateKey);
      toast.success(t('toast.apiKeyCopied'));
    }
  };

  const handleRoleChange = (event) => {
    setSelectedRoles(event.target.value);
  };

  const handleEditRoleChange = (event) => {
    setEditSelectedRoles(event.target.value);
  };

  const getRoleKeys = (permissionLevel: string) => {
    // Convert permission_level (comma-separated role names) to role keys
    const roleKeys: string[] = [];
    if (permissionLevel) {
      const roleNames = permissionLevel.split(',').map((r: string) => r.trim());
      roleNames.forEach((roleName: string) => {
        const roleKey = roleNameToKey(roleName);
        if (roleKey && availableRoles.includes(roleName)) {
          roleKeys.push(roleKey);
        }
      });
    }
    return roleKeys;
  };

  useEffect(() => {
    if (editingApiKey) {
      const permissionLevel = editingApiKey.get('permission_level') || '';
      const roleKeys = getRoleKeys(permissionLevel);
      setEditSelectedRoles(roleKeys);
      setEditApiKeyName(editingApiKey.get('name') || '');
    }
  }, [editingApiKey]);

  const handleEditApiKey = (apiKey: any) => {
    setEditingApiKey(apiKey);
    setIsEditApiKeyModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditApiKeyModalOpen(false);
    setEditingApiKey(null);
    setEditApiKeyName('');
    setEditSelectedRoles([]);
  };

  const handleUpdateApiKey = () => {
    if (editingApiKey) {
      dispatch(updateApiKey(editingApiKey.get('id'), editApiKeyName || null, editSelectedRoles));
      handleCloseEditModal();
    }
  };

  const hasResult = newApiKeyToken || newApiKeyCert;

  return (
    <div id='api-keys-screen' className='api-keys-screen page'>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper>
            <h2 >
              {t('apiKeys.manageApiKeys')}
            </h2>
            <div className='inner-section'>
              <div className='select-device-bar'>
              </div>
              <div className='api-keys-list-container'>
                <ApiKeysList onEdit={handleEditApiKey} />
              </div>
              <div className='button-bar'>
                <Button cta backgroundColor='primary'
                  icon={<AddIcon />}
                  onClick={() => setIsAddApiKeyModalOpen(true)}
                >
                  {t('apiKeys.addApiKey')}
                </Button>
              </div>
            </div>
          </Paper>
        </Grid>
      </Grid>
      <Modal
        open={isAddApiKeyModalOpen}
        onClose={handleCloseModal}
        aria-labelledby='api-key-modal-title'
      >
        <div className='modal api-keys-modal' style={{ minWidth: '500px', minHeight: '250px' }}>
          <Typography variant="h4" component="h2" gutterBottom>
            {t('apiKeys.createApiKey')}
          </Typography>

          {!hasResult ? (
            <>
              <Box mt={3} mb={3}>
                <TextField
                  fullWidth
                  label={t('apiKeys.apiKeyNameOptional')}
                  value={apiKeyName}
                  onChange={(e) => setApiKeyName(e.target.value)}
                  variant="outlined"
                  margin="normal"
                />
              </Box>
              <Box mb={3}>
                <FormControl fullWidth>
                  <InputLabel>{'test'}</InputLabel>
                  <Select
                    value={keyType}
                    onChange={(e) => setKeyType(e.target.value as 'token' | 'x509')}
                  >
                    <MenuItem value="token">{t('apiKeys.typeToken')}</MenuItem>
                    <MenuItem value="x509">{t('apiKeys.typeX509')}</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {keyType === 'x509' && (
                <Box mt={3} mb={3} display="flex" gap={1}>
                  <TextField
                    type="number"
                    label={t('apiKeys.validityDuration')}
                    placeholder={t('apiKeys.validityPlaceholder')}
                    value={expiryValue}
                    onChange={(e) => setExpiryValue(e.target.value)}
                    variant="outlined"
                    inputProps={{ min: 1 }}
                    style={{ flex: 1 }}
                  />
                  <FormControl style={{ flex: 1 }}>
                    <InputLabel>{t('apiKeys.validityTimePeriod')}</InputLabel>
                    <Select
                      value={expiryUnit}
                      onChange={(e) => setExpiryUnit(e.target.value as 'days' | 'months' | 'years')}
                    >
                      <MenuItem value="days">{t('apiKeys.validityDays')}</MenuItem>
                      <MenuItem value="months">{t('apiKeys.validityMonths')}</MenuItem>
                      <MenuItem value="years">{t('apiKeys.validityYears')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}
              <Box mt={3} mb={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('apiKeys.selectRolesOptional')}</InputLabel>
                  <Select
                    multiple
                    value={selectedRoles}
                    onChange={handleRoleChange}
                    renderValue={(selected) => (
                      <Box className='role-chips-container'>
                        {selected.map((value) => (
                          <Chip key={value} label={getRoleLabelFromKey(value, availableRoles)} size="small" style={{ fontSize: '1.3rem' }} />
                        ))}
                      </Box>
                    )}
                  >
                    {availableRoles.map((role) => {
                      return (
                        <MenuItem key={role} value={rolesNameToKey(role)}>
                          {getRoleLabel(role)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>

              <Box className='generate-api-key-button-container' display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  onClick={handleCloseModal}
                  disabled={creationLoading}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  cta
                  backgroundColor="primary"
                  onClick={handleCreateApiKey}
                  disabled={creationLoading}
                >
                  {creationLoading ? t('apiKeys.generating') : t('apiKeys.generateApiKey')}
                </Button>
              </Box>
            </>
          ) : newApiKeyCert ? (
            <>
              <Typography
                variant="h5"
                gutterBottom
                className='warning-text'
                style={{ color: variables.error, textAlign: 'center' }}
              >
                {t('apiKeys.copyCertWarning')}
              </Typography>

              <Box mt={2} mb={3}>
                <TextField
                  fullWidth
                  label={t('apiKeys.yourNewCertificate')}
                  value={newApiKeyCert}
                  multiline
                  rows={6}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <IconButton onClick={handleCopyCert} size="small">
                        <ContentCopyIcon />
                      </IconButton>
                    ),
                  }}
                  variant="outlined"
                />
              </Box>

              <Box mt={2} mb={3}>
                <TextField
                  fullWidth
                  label={t('apiKeys.yourNewPrivateKey')}
                  value={newApiKeyPrivateKey}
                  multiline
                  rows={6}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <IconButton onClick={handleCopyPrivateKey} size="small">
                        <ContentCopyIcon />
                      </IconButton>
                    ),
                  }}
                  variant="outlined"
                />
              </Box>

              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  cta
                  backgroundColor="primary"
                  onClick={handleCloseModal}
                >
                  {t('apiKeys.done')}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography
                variant="h5"
                gutterBottom
                className='warning-text'
                style={{ color: variables.error, textAlign: 'center' }}
              >
                {t('apiKeys.copyKeyWarning')}
              </Typography>

              <Box mt={2} mb={3}>
                <TextField
                  fullWidth
                  label={t('apiKeys.yourNewApiKey')}
                  value={newApiKeyToken}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <IconButton onClick={handleCopyToken} size="small">
                        <ContentCopyIcon />
                      </IconButton>
                    ),
                  }}
                  variant="outlined"
                />
              </Box>

              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  cta
                  backgroundColor="primary"
                  onClick={handleCloseModal}
                >
                  {t('apiKeys.done')}
                </Button>
              </Box>
            </>
          )}
        </div>
      </Modal>
      <Modal
        open={isEditApiKeyModalOpen}
        onClose={handleCloseEditModal}
        aria-labelledby='edit-api-key-modal-title'
      >
        <div className='modal api-keys-modal' style={{ minWidth: '500px', minHeight: '250px' }}>
          <Typography variant="h4" component="h2" gutterBottom>
            {t('apiKeys.editApiKey')}
          </Typography>

          <Box mt={3} mb={3}>
            <TextField
              fullWidth
              label={t('apiKeys.apiKeyNameOptional')}
              value={editApiKeyName}
              onChange={(e) => setEditApiKeyName(e.target.value)}
              variant="outlined"
              margin="normal"
            />
          </Box>
          <Box mt={3} mb={3}>
            <FormControl fullWidth>
              <InputLabel>{t('apiKeys.selectRolesOptional')}</InputLabel>
              <Select
                multiple
                value={editSelectedRoles}
                onChange={handleEditRoleChange}
                renderValue={(selected) => (
                  <Box className='role-chips-container'>
                    {selected.map((value) => (
                      <Chip key={value} label={getRoleLabelFromKey(value, availableRoles)} size="small" style={{ fontSize: '1.3rem' }} />
                    ))}
                  </Box>
                )}
              >
                {availableRoles.map((role) => {
                  return (
                    <MenuItem key={role} value={rolesNameToKey(role)}>
                      {getRoleLabel(role)}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              onClick={handleCloseEditModal}
            >
              {t('common.cancel')}
            </Button>
            <Button
              cta
              backgroundColor="primary"
              onClick={handleUpdateApiKey}
            >
              {t('apiKeys.saveChanges')}
            </Button>
          </Box>
        </div>
      </Modal>
    </div>
  );
}
